import React, { useState, useEffect, useRef } from 'react'
import { useAuth } from '@/context/AuthContext'
import { useTeamSlug } from '@/context/TeamSlugContext'
import { Navigate, useParams, Link } from 'react-router-dom'
import {
	Loader2,
	User,
	Edit,
	Camera,
	Save,
	List,
	ChevronUp,
	Link as LinkIcon,
} from 'lucide-react'
import { PublicHeader } from '@/components/public/PublicHeader'
import { Button } from '@/components/ui/button'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api'
import {
	Card,
	CardHeader,
	CardTitle,
	CardContent,
	CardDescription,
} from '@/components/ui/card'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Skeleton } from '@/components/ui/skeleton'
import { getInitials } from '@/utils/reviews'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/components/ui/use-toast'
import { getStatusColorClasses } from '@/components/feature-requests/FeatureRequestGrid'
import { Badge } from '@/components/ui/badge'
import { format, formatDistanceToNow } from 'date-fns'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormMessage,
} from '@/components/ui/form'
import { ProfileData, Contribution } from '@/types/profile.types'
import { useTranslation } from '@/hooks/useTranslation'

const PublicProfilePage = () => {
	const { t } = useTranslation()
	const { session, loading, signOut } = useAuth()
	const { teamSlug: paramTeamSlug } = useParams()
	const { teamSlug: contextTeamSlug, isPortal } = useTeamSlug()
	const queryClient = useQueryClient()
	const { toast } = useToast()
	const [isEditing, setIsEditing] = useState(false)
	const [avatarPreview, setAvatarPreview] = useState<string | null>(null)
	const fileInputRef = useRef<HTMLInputElement>(null)

	// Use context teamSlug for subdomains, fallback to URL param for backward compatibility
	const teamSlug = contextTeamSlug || paramTeamSlug

	const profileFormSchema = z.object({
		name: z.string().min(1, t('publicProfile.nameCannotBeEmpty')).max(100),
		avatar: z.instanceof(FileList).optional(),
	})

	type ProfileFormValues = z.infer<typeof profileFormSchema>

	const { data: profile, isLoading: isProfileLoading } = useQuery<
		ProfileData,
		Error
	>({
		queryKey: ['publicProfile', session?.user.id],
		queryFn: () => apiClient('/api/public/profile/me'),
		enabled: !!session,
	})

	const { data: contributions, isLoading: areContributionsLoading } = useQuery<
		Contribution[],
		Error
	>({
		queryKey: ['publicContributions', session?.user.id],
		queryFn: () => apiClient('/api/public/profile/me/contributions'),
		enabled: !!session,
	})

	const form = useForm<ProfileFormValues>({
		resolver: zodResolver(profileFormSchema),
		defaultValues: {
			name: '',
		},
	})

	useEffect(() => {
		if (profile) {
			form.reset({ name: profile.name || '' })
			setAvatarPreview(profile.avatar_url || null)
		}
	}, [profile, form])

	const updateProfileMutation = useMutation({
		mutationFn: (formData: FormData) =>
			apiClient('/api/public/profile/me', {
				method: 'PUT',
				data: formData,
			}),
		onSuccess: (updatedProfile) => {
			queryClient.setQueryData(
				['publicProfile', session?.user.id],
				updatedProfile
			)
			toast({ title: t('publicProfile.profileUpdatedSuccessfully') })
			setIsEditing(false)
			form.reset({ name: updatedProfile.name || '' })
			setAvatarPreview(updatedProfile.avatar_url || null)
		},
		onError: (error: any) => {
			toast({
				variant: 'destructive',
				title: t('publicProfile.updateFailed'),
				description: error.message || t('publicProfile.couldNotUpdateProfile'),
			})
		},
	})

	const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.files && e.target.files[0]) {
			const file = e.target.files[0]
			form.setValue('avatar', e.target.files) // Set the file in the form state
			setAvatarPreview(URL.createObjectURL(file))
		}
	}

	const onSubmit = (data: ProfileFormValues) => {
		const formData = new FormData()
		if (data.name !== profile?.name) {
			formData.append('name', data.name)
		}
		if (data.avatar && data.avatar.length > 0) {
			formData.append('avatar', data.avatar[0])
		}

		if (formData.has('name') || formData.has('avatar')) {
			updateProfileMutation.mutate(formData)
		} else {
			setIsEditing(false) // No changes, just close the edit form
		}
	}

	const handleCancelEdit = () => {
		setIsEditing(false)
		form.reset({ name: profile?.name || '' }) // Reset form to original data
		setAvatarPreview(profile?.avatar_url || null) // Reset avatar preview
	}

	if (loading) {
		return (
			<div className="flex items-center justify-center p-12">
				<Loader2 className="h-8 w-8 animate-spin" />
			</div>
		)
	}

	if (!session)
		return <Navigate to={isPortal ? '/' : `/c/${teamSlug}`} replace />

	return (
		<div className="min-h-screen bg-background">
			<div className="max-w-4xl mx-auto space-y-4 sm:space-y-8 p-4 sm:p-6">
				<Card className="shadow-sm">
					<CardHeader className="pb-3 sm:pb-4 px-4 sm:px-6 pt-4 sm:pt-6">
						<div className="flex justify-between items-start">
							<div>
								<CardTitle className="text-xl sm:text-2xl flex items-center gap-2 text-foreground">
									<User className="h-5 w-5 sm:h-6 sm:w-6" /> {t('publicProfile.myProfile')}
								</CardTitle>
								<CardDescription className="text-sm sm:text-base mt-1">
									{t('publicProfile.viewAndEditProfile')}
								</CardDescription>
							</div>
						</div>
					</CardHeader>
					<CardContent className="space-y-4 sm:space-y-6 px-4 sm:px-6 pb-4 sm:pb-6">
						{isProfileLoading ? (
							<Skeleton className="h-20 sm:h-24 w-full" />
						) : (
							<Form {...form}>
								<form
									onSubmit={form.handleSubmit(onSubmit)}
									className="space-y-3 sm:space-y-4">
									{isEditing ? (
										<>
											<div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
												<div className="relative">
													<Avatar className="h-16 w-16 sm:h-20 sm:w-20 border-2 border-border">
														<AvatarImage src={avatarPreview || undefined} />
														<AvatarFallback className="text-lg sm:text-2xl bg-muted">
															{getInitials(form.getValues('name'))}
														</AvatarFallback>
													</Avatar>
													<Button
														type="button"
														size="icon"
														className="absolute bottom-0 right-0 h-6 w-6 sm:h-7 sm:w-7 rounded-full shadow-lg">
														<Camera className="h-3 w-3 sm:h-4 sm:w-4" />
													</Button>
													<FormField
														control={form.control}
														name="avatar"
														render={({ field }) => (
															<FormItem>
																<FormControl>
																	<Input
																		type="file"
																		ref={fileInputRef}
																		className="hidden"
																		accept="image/*"
																		onChange={(e) => {
																			field.onChange(e.target.files)
																			handleAvatarChange(e)
																		}}
																	/>
																</FormControl>
															</FormItem>
														)}
													/>
												</div>
												<FormField
													control={form.control}
													name="name"
													render={({ field }) => (
														<FormItem className="flex-grow w-full sm:w-auto">
															<Label htmlFor="name" className="text-sm">
																{t('publicProfile.fullName')}
															</Label>
															<FormControl>
																<Input id="name" {...field} className="h-10" />
															</FormControl>
															<FormMessage />
														</FormItem>
													)}
												/>
											</div>
											<div className="flex flex-col sm:flex-row justify-end gap-2 pt-3 sm:pt-4 border-t border-border">
												<Button
													variant="ghost"
													type="button"
													size="sm"
													onClick={handleCancelEdit}
													className="w-full sm:w-auto">
													{t('publicProfile.cancel')}
												</Button>
												<Button
													type="submit"
													size="sm"
													disabled={updateProfileMutation.isPending}
													className="w-full sm:w-auto">
													{updateProfileMutation.isPending ? (
														<Loader2 className="mr-2 h-4 w-4 animate-spin" />
													) : (
														<Save className="mr-2 h-4 w-4" />
													)}
													{t('publicProfile.saveChanges')}
												</Button>
											</div>
										</>
									) : (
										<>
											<div className="flex items-center gap-3 sm:gap-4">
												<Avatar className="h-16 w-16 sm:h-20 sm:w-20 border-2 border-border">
													<AvatarImage src={profile?.avatar_url || undefined} />
													<AvatarFallback className="text-lg sm:text-2xl bg-muted">
														{getInitials(profile?.name)}
													</AvatarFallback>
												</Avatar>
												<div>
													<p className="text-lg sm:text-xl font-semibold text-foreground">
														{profile?.name || t('publicProfile.anonymousUser')}
													</p>
													<p className="text-sm text-muted-foreground">
														{profile?.email}
													</p>
												</div>
											</div>
										</>
									)}
								</form>
							</Form>
						)}
					</CardContent>
				</Card>

				<Card className="shadow-sm">
					<CardHeader className="pb-3 sm:pb-4 px-4 sm:px-6 pt-4 sm:pt-6">
						<CardTitle className="text-xl sm:text-2xl flex items-center gap-2 text-foreground">
							<List className="h-5 w-5 sm:h-6 sm:w-6" />
							{t('publicProfile.myContributions')}
						</CardTitle>
						<CardDescription className="text-sm sm:text-base mt-1">
							{t('publicProfile.contributionsDescription')}
						</CardDescription>
					</CardHeader>
					<CardContent className="px-4 sm:px-6 pb-4 sm:pb-6">
						{areContributionsLoading ? (
							<Skeleton className="h-36 sm:h-48 w-full" />
						) : (
							<div className="space-y-2 sm:space-y-3">
								{contributions && contributions.length > 0 ? (
									contributions.map((item) => (
										<Link
											to={
												isPortal
													? `/feature/${item.id}`
													: `/c/${teamSlug}/feature/${item.id}`
											}
											key={item.id}
											className="block p-3 sm:p-4 rounded-lg border bg-card hover:bg-muted/50 transition-all duration-200 hover:shadow-md hover:scale-[1.02]">
											<div className="flex justify-between items-start mb-2 gap-2">
												<p className="font-medium text-foreground line-clamp-1 text-sm sm:text-base">
													{item.title}
												</p>
												<Badge
													variant="outline"
													className={`ml-2 flex-shrink-0 text-xs ${
														getStatusColorClasses(item.status).bg
													} ${getStatusColorClasses(item.status).text}`}>
													{item.status}
												</Badge>
											</div>
											<div className="flex justify-between items-center text-xs text-muted-foreground">
												<span>
													{t('publicProfile.submitted')}{' '}
													{formatDistanceToNow(new Date(item.created_at), {
														addSuffix: true,
													})}
												</span>
												<div className="flex items-center gap-1 bg-muted/50 px-2 py-1 rounded-full">
													<ChevronUp className="h-3 w-3" />
													<span className="font-medium">
														{item.upvote_count}
													</span>
												</div>
											</div>
										</Link>
									))
								) : (
									<div className="text-center py-8 sm:py-12 text-muted-foreground bg-muted/20 rounded-lg border-2 border-dashed border-muted">
										<p className="text-sm">
											{t('publicProfile.noContributionsYet')}
										</p>
										<p className="text-xs mt-1 opacity-75">
											{t('publicProfile.submitFirstFeatureRequest')}
										</p>
									</div>
								)}
							</div>
						)}
					</CardContent>
				</Card>
			</div>
		</div>
	)
}

export default PublicProfilePage
